import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail, PhoneCall, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'

const BLOCKING_SUBSCRIPTION_STATUSES = ['pending_payment', 'expired', 'canceled', 'failed', 'none']

function getErrorMessage(error: unknown) {
  const response = (error as { response?: { data?: { error?: string; message?: string } } })?.response
  return response?.data?.error || response?.data?.message || 'Nao foi possivel entrar agora.'
}

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect') || ''
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [authMode, setAuthMode] = useState('carregando')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    apiService
      .getAuthConfig()
      .then((response) => {
        setAuthMode(response.data?.data?.provider === 'supabase' ? 'segura' : 'modo local')
      })
      .catch(() => setAuthMode('modo local'))
  }, [])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrorMessage('')
    setLoading(true)
    try {
      const response = await apiService.login({ email, password })
      const session = response.data?.data?.session
      if (session?.access_token) {
        localStorage.setItem('auth_token', session.access_token)
        if (session.refresh_token) localStorage.setItem('refresh_token', session.refresh_token)
        else localStorage.removeItem('refresh_token')
      }
      toast.success('Acesso validado.')

      // Redirect based on subscription + onboarding status
      const me = response.data?.data?.user
      if (me) {
        if (me.needs_onboarding) {
          navigate('/app/onboarding')
          return
        }
        const subStatus = me.subscription?.status
        if (subStatus && BLOCKING_SUBSCRIPTION_STATUSES.includes(subStatus)) {
          navigate('/app/billing')
          return
        }
      }
      navigate(redirectTo || '/app')
    } catch (error) {
      const msg = getErrorMessage(error)
      setErrorMessage(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto grid min-h-screen max-w-6xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[1fr_1fr] lg:px-8">
        {/* Left panel — hero */}
        <section className="relative flex flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 p-8 text-white sm:p-10" aria-labelledby="heading-login-hero">
          <div className="absolute -top-20 -right-20 h-[300px] w-[300px] rounded-full bg-cyan-500/10 blur-3xl animate-pulse-slow" />

          <Link to="/" className="relative z-10 flex items-center gap-3 group" aria-label="AiCall.Odonto Início">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25 transition-transform group-hover:scale-105">
              <PhoneCall className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="text-xl font-bold font-display">AiCall<span className="text-cyan-400">.Odonto</span></span>
          </Link>

          <div className="relative z-10 my-12 max-w-xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-500/20 bg-cyan-500/10 px-3 py-1 text-xs font-semibold text-cyan-300 font-display">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              IA de voz para clínicas odontológicas
            </div>
            <h1 id="heading-login-hero" className="text-4xl font-extrabold tracking-tight sm:text-5xl font-display leading-tight">
              Sua clínica atendendo e ligando com IA.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-300 font-serif">
              Entre para gerenciar seus agentes de voz, disparar ligações em lote, acompanhar chamadas e ver o pipeline de pacientes.
            </p>

            <div className="mt-8 grid gap-3 text-sm text-slate-400 font-display">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-cyan-400" aria-hidden="true" />
                Recepção e atendimento 24h por voz
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-cyan-400" aria-hidden="true" />
                Disparo de ligações em lote para leads
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-cyan-400" aria-hidden="true" />
                Confirmação e follow-up automático
              </div>
            </div>
          </div>

          <p className="relative z-10 text-xs font-semibold text-slate-400 font-display">Autenticação: {authMode}</p>
        </section>

        {/* Right panel — form */}
        <section className="flex items-center justify-center" aria-labelledby="heading-login-form">
          <div className="w-full max-w-md">
            {/* Brand mark — mobile */}
            <div className="mb-6 flex items-center gap-3 lg:hidden">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg">
                <PhoneCall className="h-5 w-5" aria-hidden="true" />
              </div>
              <span className="text-lg font-bold text-slate-900 font-display">AiCall<span className="text-cyan-600">.Odonto</span></span>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <div>
                <h2 id="heading-login-form" className="text-2xl font-bold text-slate-900 font-display">Entrar</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500 font-sans">
                  Acesse sua organização com e-mail e senha.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700 font-display">E-mail</span>
                    <div className="relative mt-2">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                      <input
                        className="input pl-9 focus-visible:ring-cyan-500"
                        type="email"
                        value={email}
                        aria-label="Endereço de e-mail institucional"
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="voce@clinicadental.com"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700 font-display">Senha</span>
                    <div className="relative mt-2">
                      <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                      <input
                        className="input pl-9 pr-10 focus-visible:ring-cyan-500"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        aria-label="Sua senha de acesso"
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="mínimo de 8 caracteres"
                        autoComplete="current-password"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 rounded"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                      </button>
                    </div>
                  </label>
                </div>

                {errorMessage && (
                  <p className="mt-3 text-sm text-danger-600 font-sans">{errorMessage}</p>
                )}

                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="btn-primary mt-6 h-11 w-full font-display font-bold focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2" disabled={loading}>
                  {loading ? 'Validando...' : 'Entrar'}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </motion.button>
              </form>
            </div>

            {/* Register CTA — prominent */}
            <div className="mt-6 rounded-2xl border border-cyan-100 bg-gradient-to-r from-cyan-50 to-blue-50 p-5 text-center shadow-sm">
              <p className="text-sm font-bold text-slate-800 font-display">
                Não tem conta?
              </p>
              <p className="mt-1 text-xs text-slate-500 font-sans">
                Ative sua clínica em 5 minutos. Sem período de teste, assine e use.
              </p>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="mt-4">
                <Link
                  to="/register"
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-all hover:shadow-xl hover:shadow-cyan-500/30 focus-visible:ring-2 focus-visible:ring-cyan-500"
                >
                  Criar conta agora
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}
